import { ref, computed, onBeforeUnmount } from 'vue'

const TIMER_TOTAL = 120
const TIMER_RING_CIRCUMFERENCE = 97.4

export function useOtpInput() {
  const otpDigits = ref(['', '', '', '', '', ''])
  const otpSent = ref(false)
  const counter = ref(0)
  let intervalId = null

  const otpValue = computed(() => otpDigits.value.join(''))
  const isOtpComplete = computed(() => otpValue.value.length === 6)

  const timerDisplay = computed(() => {
    const m = Math.floor(counter.value / 60)
    const s = String(counter.value % 60).padStart(2, '0')
    return `${m}:${s}`
  })

  const timerRingOffset = computed(() => {
    const progress = counter.value / TIMER_TOTAL
    return TIMER_RING_CIRCUMFERENCE * (1 - progress)
  })

  const timerUrgent = computed(() => counter.value < 30 && counter.value > 0)

  function startCounter() {
    clearInterval(intervalId)
    counter.value = TIMER_TOTAL
    intervalId = setInterval(() => {
      if (counter.value > 0) {
        counter.value -= 1
      } else {
        clearInterval(intervalId)
      }
    }, 1000)
  }

  function stopCounter() {
    clearInterval(intervalId)
    intervalId = null
  }

  function resetOtp() {
    otpDigits.value = ['', '', '', '', '', '']
    otpSent.value = false
    counter.value = 0
    stopCounter()
  }

  function beginOtpStep() {
    otpSent.value = true
    otpDigits.value = ['', '', '', '', '', '']
    startCounter()
  }

  function focusOtpInput(index, otpRefs) {
    const ref = otpRefs[`otpInput${index}`]
    const field = Array.isArray(ref) ? ref[0] : ref
    if (!field) return
    if (typeof field.focus === 'function') {
      field.focus()
      return
    }
    field.$el?.querySelector('input')?.focus()
  }

  function fillOtpFromString(startIndex, rawValue, otpRefs) {
    const digits = [...otpDigits.value]
    const nums = String(rawValue ?? '').replace(/\D/g, '')
    if (!nums) return

    for (let i = 0; i < nums.length && startIndex + i < 6; i += 1) {
      digits[startIndex + i] = nums[i]
    }

    otpDigits.value = digits
    const lastFilled = Math.min(startIndex + nums.length - 1, 5)
    if (otpRefs) {
      setTimeout(() => focusOtpInput(lastFilled, otpRefs), 0)
    }
  }

  function handleOTPInput(index, eventOrValue, otpRefs, loginBusy) {
    if (loginBusy) return

    let raw = ''
    if (eventOrValue?.target) {
      raw = eventOrValue.target.value.replace(/[^0-9]/g, '')
    } else {
      raw = String(eventOrValue ?? '').replace(/[^0-9]/g, '')
    }

    if (raw.length > 1) {
      fillOtpFromString(index, raw, otpRefs)
      return
    }

    const digits = [...otpDigits.value]
    digits[index] = raw.slice(-1)
    otpDigits.value = digits

    if (raw && index < 5 && otpRefs) {
      setTimeout(() => focusOtpInput(index + 1, otpRefs), 0)
    }
  }

  function handleOTPPaste(index, event, otpRefs, loginBusy) {
    if (loginBusy) return
    event.preventDefault()
    const pasted = event.clipboardData?.getData('text') ?? ''
    fillOtpFromString(index, pasted, otpRefs)
  }

  function handleOTPKeydown(index, event, otpRefs, loginBusy) {
    if (loginBusy) {
      event.preventDefault()
      return
    }
    if (event.key === 'Backspace' && !otpDigits.value[index] && index > 0) {
      setTimeout(() => focusOtpInput(index - 1, otpRefs), 0)
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      focusOtpInput(index - 1, otpRefs)
    } else if (event.key === 'ArrowRight' && index < 5) {
      focusOtpInput(index + 1, otpRefs)
    }
  }

  onBeforeUnmount(() => {
    stopCounter()
  })

  return {
    otpDigits,
    otpSent,
    otpValue,
    isOtpComplete,
    counter,
    timerDisplay,
    timerRingOffset,
    timerUrgent,
    beginOtpStep,
    resetOtp,
    stopCounter,
    focusOtpInput,
    handleOTPInput,
    handleOTPPaste,
    handleOTPKeydown,
    fillOtpFromString,
  }
}
